import http from '@frontend/api/http';
import { INSTRUCTIONS_URL } from '@frontend/envConfig';

import type { UserInstruction } from '../types';

type SystemInstruction = {
  prompt_guid: string;
  prompt: string;
  prompt_sementics: string;
  prompt_name: string;
  resolve_vars: {
    variables: null;
  };
  created_at: string;
  updated_at: string;
};

function getUserInstructionsBaseUrl(customerId: string, isSystemInstructionManager: boolean) {
  if (isSystemInstructionManager) {
    return `${INSTRUCTIONS_URL}/api/v1/system-prompts`;
  }
  return `${INSTRUCTIONS_URL}/api/v1/customer/${customerId}/instructions`;
}

async function getUserInstructions(customerId: string): Promise<UserInstruction[]> {
  const response = await http.get<UserInstruction[]>(
    `${getUserInstructionsBaseUrl(customerId, false)}`
  );
  return response.data;
}

function _mapSystemInstructionToInstruction(instruction: SystemInstruction): UserInstruction {
  return {
    instruction_guid: instruction.prompt_guid,
    title: instruction.prompt_name,
    instruction: instruction.prompt,
    instruction_sementics: instruction.prompt_sementics,
    isSystemInstruction: true,
    product_sku: [],
    instance_id: [],
    updated_at: new Date(instruction.updated_at),
    created_at: new Date(instruction.created_at),
  };
}

function _mapUserInstructionToInstruction(instruction: UserInstruction): SystemInstruction {
  return {
    prompt_guid: instruction.instruction_guid,
    prompt: instruction.instruction,
    prompt_sementics: instruction.instruction_sementics,
    prompt_name: instruction.title,
    resolve_vars: {
      variables: null,
    },
    created_at: instruction.created_at.toISOString(),
    updated_at: instruction.updated_at.toISOString(),
  };
}

async function getSystemInstructions(customerId: string): Promise<UserInstruction[]> {
  const response = await http.get<SystemInstruction[]>(
    `${getUserInstructionsBaseUrl(customerId, true)}`,
    {
      withCredentials: true,
    }
  );
  return response.data.map(instruction => _mapSystemInstructionToInstruction(instruction));
}

async function getInstructions(
  customerId: string,
  isSystemInstruction: boolean
): Promise<UserInstruction[]> {
  if (isSystemInstruction) {
    return getSystemInstructions(customerId);
  }
  return getUserInstructions(customerId);
}

export class UserInstructionService {
  instanceId: string;
  customerId: string;
  isSystemInstruction: boolean;

  constructor(customerId: string, instanceId: string, isSystemInstruction: boolean) {
    this.instanceId = instanceId;
    this.customerId = customerId;
    this.isSystemInstruction = isSystemInstruction;
  }

  getUserInstructions() {
    return Object.assign(
      async () => {
        return getInstructions(this.customerId, this.isSystemInstruction);
      },
      {
        key: ['opal:user-instructions', this.instanceId, this.isSystemInstruction] as const,
      }
    );
  }

  getUserInstruction(instruction_guid: string) {
    return Object.assign(
      async () => {
        return getInstructions(this.customerId, false);
      },
      { key: ['opal:user-instructions', instruction_guid] as const }
    );
  }

  async createUserInstruction(instruction: UserInstruction) {
    const baseUrl = getUserInstructionsBaseUrl(
      this.customerId,
      Boolean(instruction.isSystemInstruction)
    );
    const instructionData = {
      title: instruction.title,
      instruction: instruction.instruction,
      instruction_sementics: instruction.instruction_sementics,
      product_sku: instruction.product_sku.join(','),
      instance_id: instruction.instance_id.join(','),
    };
    return http
      .post(
        `${baseUrl}`,
        instruction.isSystemInstruction
          ? _mapUserInstructionToInstruction(instruction)
          : instructionData
      )
      .then(resp => resp.data);
  }

  async updateUserInstruction(instruction: UserInstruction) {
    const baseUrl = getUserInstructionsBaseUrl(
      this.customerId,
      Boolean(instruction.isSystemInstruction)
    );
    const instructionData = {
      title: instruction.title,
      instruction: instruction.instruction,
      instruction_sementics: instruction.instruction_sementics,
      deactivated: instruction.deactivated,
      product_sku: instruction.product_sku.join(','),
      instance_id: instruction.instance_id.join(','),
    };
    return http
      .put(
        `${baseUrl}/${instruction.instruction_guid}`,
        instruction.isSystemInstruction
          ? _mapUserInstructionToInstruction(instruction)
          : instructionData
      )
      .then(resp => resp.data);
  }

  async deleteUserInstruction(instructionGuid: string, isSystemInstruction: boolean) {
    const baseUrl = getUserInstructionsBaseUrl(this.customerId, isSystemInstruction);
    return http.delete(`${baseUrl}/${instructionGuid}`).then(resp => resp.data);
  }
}
